/**
 * MetroChipRow — dynamic metro quick-jump control (Coverage-Map
 * multi-metro readiness).
 *
 * Fetches `GET /api/public/metros` on mount and renders one chip per
 * active metro row. Click a chip → `mapRef.current.flyTo(primary_center)`.
 *
 * CRITICAL invariant — chips are DYNAMICALLY derived from the DB.
 * Adding a 6th metro to the `metros` collection renders a 6th chip
 * with ZERO frontend code change. Tests assert this by injecting a
 * synthetic 6-metro response and expecting 6 chips.
 *
 * When the metros list is empty (e.g. brand-new DB, still seeding,
 * or fetch failure), the component renders nothing — the map's
 * built-in national-fit fallback is the graceful degradation.
 */
import { useEffect, useState } from "react";
import { API_BASE } from "@/lib/api";

export default function MetroChipRow({ mapRef }) {
  const [metros, setMetros] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE}/public/metros`);
        if (!r.ok) return;
        const j = await r.json();
        if (cancelled) return;
        setMetros(j.metros || []);
      } catch (_e) { /* silent — graceful degradation */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!metros.length) return null;

  const handleClick = (m) => {
    const map = mapRef?.current;
    const c = m.primary_center;
    if (!map || !c || typeof c.lng !== "number" || typeof c.lat !== "number") {
      return;
    }
    setActive(m.code);
    map.flyTo({
      center: [c.lng, c.lat],
      zoom: typeof c.zoom === "number" ? c.zoom : 9,
      duration: 900,
      essential: true,
    });
  };

  return (
    <div
      className="mt-4 flex flex-wrap items-center gap-2"
      role="group"
      aria-label="Jump to metro"
      data-testid="metro-chip-row"
    >
      <span
        className="pp-sm"
        style={{ color: "var(--pp-text-soft)", marginRight: 4 }}
      >
        Jump to:
      </span>
      {metros.map((m) => {
        const isActive = active === m.code;
        return (
          <button
            key={m.code}
            type="button"
            onClick={() => handleClick(m)}
            aria-pressed={isActive}
            data-testid={`metro-chip-${m.code}`}
            className="rounded-full border px-3 py-1 text-sm font-semibold transition-colors"
            style={{
              borderColor: isActive
                ? "var(--pp-orange)"
                : "var(--pp-border)",
              background: isActive
                ? "var(--pp-orange)"
                : "var(--pp-surface)",
              color: isActive ? "var(--pp-surface)" : "var(--pp-text)",
              cursor: "pointer",
            }}
          >
            {m.label}
          </button>
        );
      })}
    </div>
  );
}
